// Fetch-Wrapper für die öffentlichen SmartAI-Endpunkte des Admin-Backends
// (Muster: bookingPublicApi.ts). Cross-Origin von seadent.es; CORS ist
// serverseitig konfiguriert. Kein Auth — bewusst unauthentifiziert.

import { PUBLIC_API_BASE_URL } from '../config/apiBase';

const API_BASE_URL = PUBLIC_API_BASE_URL;

export interface SmartAIConfig {
  enabled: boolean;
  visibility: 'preview' | 'public';
  /** 'classic' = bisheriges Panel, 'v2' = Buchung/Verwaltung im Panel selbst. */
  layout: 'classic' | 'v2';
  /**
   * Öffnungsstatus für den Panel-Kopf; openNow === null = unbekannt, dann nichts
   * anzeigen. closedUntil ('YYYY-MM-DD') ist gesetzt, solange die Praxis wegen
   * Urlaub/Betriebsferien zu ist — dann zählt das Datum, nicht die Uhrzeit.
   */
  hours: { openNow: boolean | null; opensAt: string | null; closesAt: string | null; closedUntil: string | null };
  /** Urlaub/Betriebsferien — solange aktiv, ist bookingEnabled false. */
  closure: { active: boolean; upcoming: boolean; from: string; until: string };
  llmEnabled: boolean;
  bookingEnabled: boolean;
}

export type SmartAIRequestType = 'callback' | 'prescription' | 'document';

export interface SmartAIRequestPayload {
  type: SmartAIRequestType;
  name: string;
  phone: string;
  email?: string;
  message?: string;
  preferredTime?: string;
  language: string;
  recaptchaToken: string;
}

export async function getSmartAIConfig(): Promise<SmartAIConfig | null> {
  // Fail-closed: jede Störung (Netz, CORS, 5xx) => Panel bleibt unsichtbar.
  try {
    const r = await fetch(`${API_BASE_URL}/public/smartai/config`);
    if (!r.ok) return null;
    const j = await r.json();
    if (!j || typeof j.enabled !== 'boolean') return null;
    return {
      enabled: j.enabled,
      visibility: j.visibility === 'public' ? 'public' : 'preview',
      // Unbekannter Wert => classic. Ein neues Backend-Layout darf ein altes
      // Frontend-Bundle nie in einen ungerenderten Zustand schicken.
      layout: j.layout === 'v2' ? 'v2' : 'classic',
      hours: {
        openNow: typeof j.hours?.openNow === 'boolean' ? j.hours.openNow : null,
        opensAt: typeof j.hours?.opensAt === 'string' ? j.hours.opensAt : null,
        closesAt: typeof j.hours?.closesAt === 'string' ? j.hours.closesAt : null,
        closedUntil: typeof j.hours?.closedUntil === 'string' && j.hours.closedUntil ? j.hours.closedUntil : null,
      },
      closure: {
        active: Boolean(j.closure?.active),
        upcoming: Boolean(j.closure?.upcoming),
        from: typeof j.closure?.from === 'string' ? j.closure.from : '',
        until: typeof j.closure?.until === 'string' ? j.closure.until : '',
      },
      llmEnabled: Boolean(j.llmEnabled),
      bookingEnabled: Boolean(j.bookingEnabled),
    };
  } catch {
    return null;
  }
}

export type SmartAIIntent =
  | 'booking' | 'reschedule' | 'cancel' | 'callback'
  | 'prescription' | 'document' | 'question' | 'other';

export interface SmartAIChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface SmartAIChatResult {
  llmEnabled: boolean;
  intent?: SmartAIIntent;
  answered?: boolean;
  reply?: string;
}

export async function sendSmartAIChat(payload: {
  sessionId: string;
  message: string;
  language: string;
  history?: SmartAIChatTurn[];
  recaptchaToken: string;
}): Promise<SmartAIChatResult> {
  const r = await fetch(`${API_BASE_URL}/public/smartai/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    body: JSON.stringify(payload),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err: any = new Error(j?.message || `chat failed: ${r.status}`);
    err.status = r.status;
    throw err;
  }
  return {
    llmEnabled: Boolean(j.llmEnabled),
    intent: j.intent,
    answered: j.answered,
    reply: j.reply,
  };
}

export async function submitSmartAIQuestion(payload: {
  question: string;
  email?: string;
  name?: string;
  language: string;
  recaptchaToken: string;
}): Promise<void> {
  const r = await fetch(`${API_BASE_URL}/public/smartai/questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j?.message || `question failed: ${r.status}`);
  }
}

export async function requestSmartAIManageLink(payload: {
  email: string;
  language: string;
  recaptchaToken: string;
}): Promise<void> {
  // Antwort ist absichtlich immer identisch (Enumeration-Schutz serverseitig).
  const r = await fetch(`${API_BASE_URL}/public/smartai/manage-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error(`manage-link failed: ${r.status}`);
}

export async function submitSmartAIRequest(payload: SmartAIRequestPayload): Promise<void> {
  const r = await fetch(`${API_BASE_URL}/public/smartai/requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j?.message || `smartai request failed: ${r.status}`);
  }
}

// --- Bestätigung per E-Mail-Code ---------------------------------------------

export interface SmartAIVerifiedAppointment {
  /** Token für die bestehenden Self-Service-Endpunkte (selfserviceApi.ts). */
  manageToken: string;
  date: string;   // 'YYYY-MM-DD'
  time: string;   // 'HH:mm'
  status: string;
  procedureName: string;
}

export type SmartAIVerifyError = 'invalid' | 'expired' | 'locked' | 'error';

/**
 * Code anfordern. Antwortet immer gleich — ob die Adresse bekannt ist, erfährt
 * der Aufrufer bewusst nicht.
 */
export async function requestSmartAIVerifyCode(payload: {
  email: string; language: string; recaptchaToken: string;
}): Promise<void> {
  await fetch(`${API_BASE_URL}/public/smartai/verify/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    body: JSON.stringify(payload),
  });
}

/** Code prüfen. Bei Erfolg kommen die kommenden Termine samt Verwaltungstoken. */
export async function confirmSmartAIVerifyCode(payload: {
  email: string; code: string; language: string;
}): Promise<{ ok: true; appointments: SmartAIVerifiedAppointment[] } | { ok: false; reason: SmartAIVerifyError }> {
  try {
    const r = await fetch(`${API_BASE_URL}/public/smartai/verify/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(payload),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j?.success) {
      const reason = ['invalid', 'expired', 'locked'].includes(j?.code) ? j.code : 'error';
      return { ok: false, reason };
    }
    return { ok: true, appointments: Array.isArray(j.appointments) ? j.appointments : [] };
  } catch {
    return { ok: false, reason: 'error' };
  }
}
